import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi, optimierungApi, radarApi, actionsApi, financialImpactApi, notificationsApi, bankStatementsApi } from '../api';
import PushSettings from '../components/PushSettings';
import InboxWidget from '../components/InboxWidget';

const AMPEL_FARBE = {
  dunkelrot: { bg: '#7f1d1d', border: '#dc2626', text: '#fca5a5' },
  rot:       { bg: '#450a0a', border: '#ef4444', text: '#fca5a5' },
  orange:    { bg: '#431407', border: '#f97316', text: '#fdba74' },
  gelb:      { bg: '#422006', border: '#eab308', text: '#fde047' },
  gruen:     { bg: '#052e16', border: '#22c55e', text: '#86efac' },
  grau:      { bg: '#1e293b', border: '#475569', text: '#94a3b8' },
};

function FristenDashboard({ onNav }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    dashboardApi.fristen()
      .then(r => setData(r.data))
      .catch(() => setErr('Fristen-Daten konnten nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (err || !data) return null;

  const { wichtige_fristen, kuendigungsfrist_laeuft, auto_verlaengerungen, monatsvorschau, empfehlungen } = data;

  return (
    <div style={{ marginBottom: 14 }}>
      <DashboardCard title="Fristen-Übersicht">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))', gap: 14 }}>

          {/* A: Wichtige Fristen */}
          {wichtige_fristen?.length > 0 && (
            <div>
              <h3 style={{ color: '#cbd5e1', fontSize: 13, margin: '0 0 10px', fontWeight: 700 }}>Wichtige Fristen</h3>
              <div style={{ display: 'grid', gap: 8 }}>
                {wichtige_fristen.slice(0, 5).map(v => {
                  const f = AMPEL_FARBE[v.ampel] || AMPEL_FARBE.grau;
                  return (
                    <button key={v.id} onClick={() => onNav(`/vertraege/${v.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                        background: f.bg, border: `1px solid ${f.border}`, borderRadius: 10,
                        padding: '10px 12px', cursor: 'pointer', textAlign: 'left' }}>
                      <span style={{ color: f.text, fontWeight: 700, fontSize: 14, minWidth: 30 }}>
                        {v.deadline_tage !== null ? (v.deadline_tage >= 0 ? `${v.deadline_tage}T` : 'abg.') : '–'}
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', color: '#f1f5f9', fontWeight: 600, fontSize: 13 }}>{v.titel}</span>
                        <span style={{ display: 'block', color: '#94a3b8', fontSize: 12 }}>{v.anbieter || ''} · {v.letzte_kuendigung_moeglich || v.vertragsende || ''}</span>
                      </span>
                      <span style={{ color: f.text, fontSize: 11, fontWeight: 700, padding: '2px 6px',
                        background: `${f.border}22`, borderRadius: 8, whiteSpace: 'nowrap' }}>
                        {v.dringlichkeit || v.status}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* B: Kündigungsfrist läuft */}
          {kuendigungsfrist_laeuft?.length > 0 && (
            <div>
              <h3 style={{ color: '#cbd5e1', fontSize: 13, margin: '0 0 10px', fontWeight: 700 }}>Kündigungsfrist läuft</h3>
              <div style={{ display: 'grid', gap: 8 }}>
                {kuendigungsfrist_laeuft.slice(0, 5).map(v => (
                  <button key={v.id} onClick={() => onNav(`/vertraege/${v.id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                      background: '#1c0a00', border: '1px solid #f97316', borderRadius: 10,
                      padding: '10px 12px', cursor: 'pointer', textAlign: 'left' }}>
                    <span style={{ fontSize: 20 }}>✂️</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', color: '#f1f5f9', fontWeight: 600, fontSize: 13 }}>{v.titel}</span>
                      <span style={{ display: 'block', color: '#94a3b8', fontSize: 12 }}>
                        {v.letzte_kuendigung_moeglich} · {v.deadline_tage >= 0 ? `noch ${v.deadline_tage} Tag(e)` : 'Frist abgelaufen'}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* C: Auto-Verlängerungen */}
          {auto_verlaengerungen?.length > 0 && (
            <div>
              <h3 style={{ color: '#cbd5e1', fontSize: 13, margin: '0 0 10px', fontWeight: 700 }}>Automatische Verlängerungen</h3>
              <div style={{ display: 'grid', gap: 8 }}>
                {auto_verlaengerungen.slice(0, 4).map(v => (
                  <button key={v.id} onClick={() => onNav(`/vertraege/${v.id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                      background: '#0c1a33', border: '1px solid #818cf8', borderRadius: 10,
                      padding: '10px 12px', cursor: 'pointer', textAlign: 'left' }}>
                    <span style={{ fontSize: 20 }}>🔄</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', color: '#f1f5f9', fontWeight: 600, fontSize: 13 }}>{v.titel}</span>
                      <span style={{ display: 'block', color: '#94a3b8', fontSize: 12 }}>
                        Verlängerung: {v.naechste_verlaengerung || '–'} · +{v.verlaengerungszeitraum_monate || '?'} Mo.
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* D: Monatsvorschau */}
          {monatsvorschau?.length > 0 && (
            <div>
              <h3 style={{ color: '#cbd5e1', fontSize: 13, margin: '0 0 10px', fontWeight: 700 }}>Monatsvorschau Fristen</h3>
              <div style={{ display: 'grid', gap: 6 }}>
                {monatsvorschau.slice(0, 6).map(m => (
                  <div key={m.monat} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #1e293b' }}>
                    <span style={{ color: '#94a3b8', fontSize: 12, minWidth: 60 }}>{m.monat_label || m.monat}</span>
                    <div style={{ flex: 1, display: 'flex', gap: 6 }}>
                      {m.kuendigungen > 0 && <span style={{ fontSize: 11, background: '#ef444433', color: '#fca5a5', padding: '1px 6px', borderRadius: 8 }}>{m.kuendigungen} Kündg.</span>}
                      {m.vertragsenden > 0 && <span style={{ fontSize: 11, background: '#f9731633', color: '#fdba74', padding: '1px 6px', borderRadius: 8 }}>{m.vertragsenden} Ende</span>}
                      {m.verlaengerungen > 0 && <span style={{ fontSize: 11, background: '#818cf833', color: '#c7d2fe', padding: '1px 6px', borderRadius: 8 }}>{m.verlaengerungen} Verl.</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* F: Handlungsempfehlungen */}
          {empfehlungen?.length > 0 && (
            <div>
              <h3 style={{ color: '#cbd5e1', fontSize: 13, margin: '0 0 10px', fontWeight: 700 }}>Handlungsempfehlungen</h3>
              <div style={{ display: 'grid', gap: 8 }}>
                {empfehlungen.slice(0, 4).map((e, i) => (
                  <button key={i} onClick={() => e.url && onNav(e.url)}
                    className="btn-secondary" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%', cursor: e.url ? 'pointer' : 'default' }}>
                    <span style={{ fontSize: 18 }}>{e.icon || '💡'}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', color: '#f1f5f9', fontWeight: 600, fontSize: 13 }}>{e.titel}</span>
                      {e.beschreibung && <span style={{ display: 'block', color: '#94a3b8', fontSize: 12, marginTop: 2 }}>{e.beschreibung}</span>}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

        </div>
      </DashboardCard>
    </div>
  );
}

export default function DashboardPage() {
  const [cockpit, setCockpit] = useState(null);
  const [dokKpis, setDokKpis] = useState(null);
  const [kuendigungsKpis, setKuendigungsKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      dashboardApi.cockpit(),
      dashboardApi.dokumente().catch(() => null),
      dashboardApi.kuendigungen().catch(() => null),
    ]).then(([cockpitRes, dokRes, kuendigRes]) => {
      setCockpit(cockpitRes.data);
      if (dokRes?.data) setDokKpis(dokRes.data.kpis);
      if (kuendigRes?.data) setKuendigungsKpis(kuendigRes.data.kpis);
    }).catch(() => setError('Dashboard konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingScreen />;
  if (error) return <StateScreen title="Dashboard nicht verfügbar" text={error} />;
  if (!cockpit) return <StateScreen title="Keine Daten" text="Für das Cockpit liegen noch keine Daten vor." />;

  const importantCount = cockpit.greeting?.wichtige_ereignisse || 0;
  const hasData = cockpit.kpis?.aktive_vertraege || cockpit.kpis?.dokumente || cockpit.events?.length;

  return (
    <div style={{ padding: 18, maxWidth: 1180, margin: '0 auto' }}>
      <section style={heroStyle}>
        <div>
          <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 8 }}>Vertrags-Cockpit</div>
          <h1 style={{ color: '#f8fafc', fontSize: 30, lineHeight: 1.15, margin: 0 }}>
            {cockpit.greeting?.message || 'Guten Tag'}
          </h1>
          <p style={{ color: '#cbd5e1', fontSize: 15, margin: '10px 0 0' }}>
            Heute gibt es {importantCount} wichtige Ereignisse.
          </p>
        </div>
        <div style={heroActions}>
          <button className="btn-primary" onClick={() => navigate('/vertraege/neu')}>+ Vertrag</button>
          <button className="btn-secondary" onClick={() => navigate('/vertraege/scan')}>🤖 Scan</button>
        </div>
      </section>

      <section style={kpiGrid}>
        <KpiCard label="Aktive Verträge" value={cockpit.kpis.aktive_vertraege} icon="📋" tone="#38bdf8" onClick={() => navigate('/vertraege')} />
        <KpiCard label="Bald kündbar" value={cockpit.kpis.bald_kuendbar} icon="⏰" tone="#fbbf24" onClick={() => navigate('/vertraege')} />
        <KpiCard label="Abgelaufen" value={cockpit.kpis.abgelaufen} icon="⚠️" tone="#f87171" onClick={() => navigate('/vertraege')} />
        <KpiCard label="Monatliche Kosten" value={formatEuro(cockpit.kpis.monatliche_kosten)} icon="💶" tone="#34d399" onClick={() => navigate('/reports')} />
        <KpiCard label="Jahreskosten" value={formatEuro(cockpit.kpis.jaehrliche_kosten)} icon="📆" tone="#818cf8" onClick={() => navigate('/reports')} />
        <KpiCard label="Dokumente" value={`${cockpit.kpis.dokumente} / ${cockpit.kpis.ocr_dokumente} OCR`} icon="📄" tone="#22d3ee" onClick={() => navigate('/dokumente')} />
      </section>

      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <KpiCard label="Kündigung ≤30 Tage" value={cockpit.kpis.kuendigungen_30_tage ?? 0} icon="🔴" tone="#ef4444" onClick={() => navigate('/kuendigungen')} />
        <KpiCard label="Kündigung ≤90 Tage" value={cockpit.kpis.kuendigungen_90_tage ?? 0} icon="🟡" tone="#f59e0b" onClick={() => navigate('/kuendigungen')} />
        <KpiCard label="Auto-verlängert" value={cockpit.kpis.auto_verlaengert ?? 0} icon="🔄" tone="#818cf8" onClick={() => navigate('/vertraege')} />
        <KpiCard label="Ohne Dokument" value={cockpit.kpis.vertraege_ohne_dokumente ?? 0} icon="📭" tone="#64748b" onClick={() => navigate('/vertraege')} />
      </section>

      {!hasData && (
        <DashboardCard title="Starten">
          <EmptyState text="Noch keine Verträge im Cockpit. Lege den ersten Vertrag an oder scanne ein Dokument." />
        </DashboardCard>
      )}

      <FristenDashboard onNav={navigate} />

      <ActionsWidget onNav={navigate} />

      <NotificationWidget onNav={navigate} />

      <BankStatementWidget onNav={navigate} />

      <FinancialWidget onNav={navigate} />

      <InboxWidget />

      <RadarWidget onNav={navigate} />

      <OptimierungWidget onNav={navigate} />

      <div style={mainGrid}>
        <div style={{ display: 'grid', gap: 14, alignContent: 'start' }}>
          <DashboardCard title="Action Center">
            {cockpit.actionCenter?.length ? (
              <div style={{ display: 'grid', gap: 10 }}>
                {cockpit.actionCenter.map((action, index) => (
                  <ActionCard key={`${action.url}-${index}`} action={action} onClick={() => navigate(action.url)} />
                ))}
              </div>
            ) : (
              <EmptyState text="Keine dringenden Aufgaben. Deine Vertragsakte sieht aktuell ruhig aus." />
            )}
          </DashboardCard>

          <DashboardCard title="Empfehlungen">
            {cockpit.recommendations?.length ? (
              <div style={{ display: 'grid', gap: 10 }}>
                {cockpit.recommendations.map((recommendation, index) => (
                  <RecommendationCard key={`${recommendation.url}-${index}`} recommendation={recommendation} onClick={() => navigate(recommendation.url)} />
                ))}
              </div>
            ) : (
              <EmptyState text="Aktuell keine regelbasierten Empfehlungen." />
            )}
          </DashboardCard>

          <DashboardCard title="Dokumente">
            <DocumentQueues cockpit={cockpit} onOpen={(url) => navigate(url)} />
          </DashboardCard>

          {dokKpis && (dokKpis.fehlgeschlagen > 0 || dokKpis.ausstehend > 0 || dokKpis.risiko_hoch > 0 || dokKpis.dubletten > 0) && (
            <DashboardCard title="Dokumentenanalyse">
              <DokumentAnalyseWidget kpis={dokKpis} onNav={navigate} />
            </DashboardCard>
          )}

          {kuendigungsKpis && kuendigungsKpis.gesamt > 0 && (
            <DashboardCard title="Kündigungsvorgänge">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10, marginBottom: 10 }}>
                {[
                  { label: 'Gesamt', value: kuendigungsKpis.gesamt, tone: '#94a3b8' },
                  { label: 'Entwurf', value: kuendigungsKpis.entwurf, tone: '#64748b' },
                  { label: 'Freigegeben', value: kuendigungsKpis.freigegeben, tone: '#38bdf8' },
                  { label: 'Versendet', value: kuendigungsKpis.versendet, tone: '#34d399' },
                  { label: 'Diese Woche', value: kuendigungsKpis.diese_woche_faellig, tone: '#fbbf24' },
                  { label: 'Wiedervorlagen', value: kuendigungsKpis.wiedervorlagen_offen, tone: '#f87171' },
                ].map(item => (
                  <div key={item.label} style={{ background: '#0f172a', borderRadius: 8, padding: '8px 12px', borderLeft: `3px solid ${item.tone}` }}>
                    <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 2 }}>{item.label}</div>
                    <div style={{ color: item.tone, fontSize: 22, fontWeight: 700 }}>{item.value || 0}</div>
                  </div>
                ))}
              </div>
              <button onClick={() => navigate('/kuendigungen')} className="btn-secondary">
                Alle Vorgänge →
              </button>
            </DashboardCard>
          )}

          <DashboardCard title="KI-Assistent">
            <AssistantWidgets
              assistant={cockpit.assistant}
              onOpen={(message) => navigate(`/assistant?message=${encodeURIComponent(message)}`)}
              onOpenSettings={() => navigate('/assistant')}
            />
          </DashboardCard>
        </div>

        <div style={{ display: 'grid', gap: 14, alignContent: 'start' }}>
          <DashboardCard title="Ereignis-Timeline">
            {cockpit.events?.length ? (
              <div style={{ display: 'grid', gap: 10 }}>
                {cockpit.events.map((event, index) => (
                  <TimelineItem key={`${event.url}-${event.date}-${index}`} event={event} onClick={() => navigate(event.url)} />
                ))}
              </div>
            ) : (
              <EmptyState text="Keine Ereignisse vorhanden." />
            )}
          </DashboardCard>

          <DashboardCard title="Analyse">
            <AnalysisCharts charts={cockpit.charts} />
          </DashboardCard>
        </div>
      </div>

      <DashboardCard title="Schnellzugriff">
        <div style={quickGrid}>
          {[
            { label: '🤖 Vertrag scannen', path: '/vertraege/scan', color: '#10b981' },
            { label: '🤖 KI-Assistent', path: '/assistant', color: '#38bdf8' },
            { label: '📄 Dokumente', path: '/dokumente', color: '#22d3ee' },
            { label: '📄 Kontoauszug', path: '/kontoauszug', color: '#38bdf8' },
            { label: '📑 Reports', path: '/reports', color: '#f59e0b' },
            { label: '📧 Finanzbericht', path: '/finanzbericht', color: '#34d399' },
            { label: '📅 Ausgabenplaner', path: '/ausgabenplaner', color: '#818cf8' },
            { label: '📊 Auswertungen', path: '/zahlungen/auswertung', color: '#22d3ee' },
          ].map(item => (
            <button key={item.path} onClick={() => navigate(item.path)} style={quickBtn(item.color)}>{item.label}</button>
          ))}
        </div>
      </DashboardCard>

      <PushSettings />
    </div>
  );
}

const CLOUD_MODE_LABELS = {
  lokal_only: 'Nur lokal',
  cloud_allowed: 'Cloud erlaubt',
  cloud_confirm: 'Cloud nach Bestätigung',
};

function AssistantWidgets({ assistant, onOpen, onOpenSettings }) {
  const items = [
    { title: 'Letzte KI-Fragen', value: assistant?.letzte_fragen?.length || 0, question: 'Was waren meine letzten KI-Fragen?' },
    { title: 'Offene Risiken', value: assistant?.offene_risiken?.length || 0, question: 'Welche Risiken gibt es?' },
    { title: 'Neue Erkenntnisse', value: assistant?.neue_erkenntnisse?.length || 0, question: 'Welche neuen Erkenntnisse gibt es?' },
    { title: 'Empfohlene Kündigungen', value: assistant?.empfohlene_kuendigungen?.length || 0, question: 'Welche Kündigungen sind empfohlen?' },
  ];
  const status = assistant?.provider_status;
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={assistantGrid}>
        {items.map(item => (
          <button key={item.title} onClick={() => onOpen(item.question)} className="btn-secondary" style={{ display: 'grid', gap: 5 }}>
            <strong style={{ color: '#38bdf8', fontSize: 20 }}>{item.value}</strong>
            <span style={{ color: '#cbd5e1', fontSize: 13 }}>{item.title}</span>
          </button>
        ))}
      </div>
      {status && (
        <button onClick={onOpenSettings} className="btn-secondary" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', width: '100%', boxSizing: 'border-box' }}>
          <span>
            <strong style={{ color: '#f8fafc' }}>Aktiver Provider: {status.activeProvider}</strong>
            <span style={{ color: '#94a3b8' }}> · {CLOUD_MODE_LABELS[status.cloudMode] || status.cloudMode}</span>
          </span>
          <span style={{ color: '#94a3b8', fontSize: 12 }}>
            {status.configuredCloudProviders} von {status.providers.filter(p => p.cloud).length} Cloud-Providern konfiguriert
          </span>
        </button>
      )}
    </div>
  );
}

function DashboardCard({ title, children }) {
  return (
    <section style={cardStyle}>
      <h2 style={cardTitle}>{title}</h2>
      {children}
    </section>
  );
}

function KpiCard({ label, value, icon, tone, onClick }) {
  return (
    <button onClick={onClick} style={{ ...kpiCard, borderColor: `${tone}55` }}>
      <span style={{ fontSize: 24 }}>{icon}</span>
      <span style={{ color: tone, fontSize: 20, fontWeight: 800, marginTop: 10 }}>{value}</span>
      <span style={{ color: '#94a3b8', fontSize: 12, marginTop: 5 }}>{label}</span>
    </button>
  );
}

function ActionCard({ action, onClick }) {
  const color = action.priority === 'high' ? '#f87171' : action.priority === 'medium' ? '#fbbf24' : '#38bdf8';
  return (
    <button onClick={onClick} className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%' }}>
      <span style={{ ...priorityDot, background: color }} />
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'block', color: '#f8fafc', fontWeight: 800, fontSize: 14 }}>{action.title}</span>
        <span style={{ display: 'block', color: '#94a3b8', fontSize: 12, marginTop: 4 }}>{action.subtitle}</span>
      </span>
      <span style={{ color, fontSize: 12, fontWeight: 700 }}>{action.actionLabel}</span>
    </button>
  );
}

function TimelineItem({ event, onClick }) {
  const color = {
    danger: '#f87171',
    warning: '#fbbf24',
    success: '#34d399',
    info: '#38bdf8',
  }[event.type] || '#94a3b8';
  return (
    <button onClick={onClick} className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
      <span style={{ ...timelineIcon, background: `${color}22`, color }}>{eventIcon(event.type)}</span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'block', color: '#f8fafc', fontWeight: 800, fontSize: 14 }}>{event.title}</span>
        <span style={{ display: 'block', color: '#94a3b8', fontSize: 12, marginTop: 4 }}>{event.subtitle}</span>
      </span>
      <span style={{ color: '#64748b', fontSize: 12 }}>{formatDate(event.date)}</span>
    </button>
  );
}

function RecommendationCard({ recommendation, onClick }) {
  const color = recommendation.severity === 'danger' ? '#f87171' : recommendation.severity === 'warning' ? '#fbbf24' : '#38bdf8';
  return (
    <button onClick={onClick} className="btn-secondary" style={{ width: '100%', borderColor: `${color}44` }}>
      <div style={{ color, fontSize: 12, fontWeight: 800, textTransform: 'uppercase' }}>{recommendation.severity}</div>
      <div style={{ color: '#f8fafc', fontWeight: 800, marginTop: 5 }}>{recommendation.title}</div>
      <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 6 }}>{recommendation.text}</div>
    </button>
  );
}

function DokumentAnalyseWidget({ kpis, onNav }) {
  const items = [
    { label: 'Analysiert', value: kpis.abgeschlossen, color: '#22c55e', icon: '✓' },
    { label: 'Ausstehend', value: kpis.ausstehend, color: '#6366f1', icon: '⏳' },
    { label: 'Fehler', value: kpis.fehlgeschlagen, color: '#ef4444', icon: '⛔' },
    { label: 'Hohes Risiko', value: kpis.risiko_hoch, color: '#f97316', icon: '⚠️' },
    { label: 'Dubletten', value: kpis.dubletten, color: '#f59e0b', icon: '⊕' },
  ];
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 8, marginBottom: 4 }}>
        {items.map(item => (
          <div key={item.label} style={{ background: item.color + '18', border: `1px solid ${item.color}44`,
            borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
            <div style={{ color: item.color, fontSize: 18 }}>{item.icon}</div>
            <div style={{ color: item.color, fontWeight: 800, fontSize: 18 }}>{item.value}</div>
            <div style={{ color: '#64748b', fontSize: 11 }}>{item.label}</div>
          </div>
        ))}
      </div>
      {(kpis.fehlgeschlagen > 0 || kpis.risiko_hoch > 0) && (
        <button onClick={() => onNav('/dokumente')}
          style={{ background: '#431407', border: '1px solid #ef4444', borderRadius: 8, color: '#fca5a5',
            padding: '9px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, textAlign: 'left' }}>
          {kpis.fehlgeschlagen > 0 && `${kpis.fehlgeschlagen} Analyse${kpis.fehlgeschlagen > 1 ? 'n' : ''} fehlgeschlagen`}
          {kpis.fehlgeschlagen > 0 && kpis.risiko_hoch > 0 && ' · '}
          {kpis.risiko_hoch > 0 && `${kpis.risiko_hoch} Dokument${kpis.risiko_hoch > 1 ? 'e' : ''} mit hohem Risiko`}
        </button>
      )}
      {kpis.ausstehend > 0 && (
        <button onClick={() => onNav('/dokumente')}
          style={{ background: '#1e1b4b', border: '1px solid #6366f1', borderRadius: 8, color: '#a5b4fc',
            padding: '9px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, textAlign: 'left' }}>
          {kpis.ausstehend} Dokument{kpis.ausstehend > 1 ? 'e' : ''} warten auf Analyse →
        </button>
      )}
    </div>
  );
}

function DocumentQueues({ cockpit, onOpen }) {
  const dokumente = cockpit?.dokumente || {};
  const queues = [
    { title: 'Neue Dokumente', items: dokumente?.neue || [], tone: '#38bdf8' },
    { title: 'Neue KI-Analysen', items: dokumente?.neue_ki_analysen || [], tone: '#22d3ee' },
    { title: 'OCR offen', items: dokumente?.ocr_offen || [], tone: '#fbbf24' },
    { title: 'KI offen', items: dokumente?.ki_offen || [], tone: '#a78bfa' },
    { title: 'Risikodokumente', items: dokumente?.risikodokumente || [], tone: '#fb7185' },
    { title: 'Ohne Analyse', items: dokumente?.vertraege_ohne_analyse || [], tone: '#f97316' },
    { title: 'Fehler', items: dokumente?.dokumente_mit_fehler || [], tone: '#ef4444' },
    { title: 'Wichtig', items: dokumente?.wichtige || [], tone: '#34d399' },
    { title: 'Ohne Vertrag', items: dokumente?.ohne_vertrag || [], tone: '#f87171' },
  ];
  const hasItems = queues.some(queue => queue.items.length > 0);
  if (!hasItems) return <EmptyState text="Keine Dokumentenaufgaben vorhanden." />;
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {queues.map(queue => (
        <div key={queue.title} style={{ display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <strong style={{ color: queue.tone, fontSize: 13 }}>{queue.title}</strong>
            <span style={{ color: '#64748b', fontSize: 12 }}>{queue.items.length}</span>
          </div>
          {queue.items.length === 0 ? (
            <EmptyState text="Keine Einträge." />
          ) : (
            queue.items.slice(0, 3).map(item => (
              <button key={`${queue.title}-${item.id}`} onClick={() => onOpen(item.url || `/dokumente?dokument=${item.id}`)} className="btn-secondary" style={{ display: 'grid', gap: 4, width: '100%' }}>
                <span style={{ color: '#f8fafc', fontWeight: 800, overflowWrap: 'anywhere' }}>{item.originalname || item.originaldateiname || item.dateiname}</span>
                <span style={{ color: '#94a3b8', fontSize: 12 }}>{item.vertrag || item.vertrag_titel || ''} · {item.status_label || item.ki_status || item.ocr_status || 'offen'}</span>
              </button>
            ))
          )}
        </div>
      ))}
    </div>
  );
}

function AnalysisCharts({ charts }) {
  const categories = charts?.kostenNachKategorie || [];
  const status = charts?.status || [];
  const cancellations = charts?.kuendigungen || [];
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <MiniBars
        title="Kosten nach Kategorie"
        items={categories.map(item => ({
          label: `${item.icon || ''} ${item.name}`,
          value: item.monatliche_kosten,
          text: `${formatEuro(item.monatliche_kosten)}/Mo`,
          color: item.farbe || '#38bdf8',
        }))}
      />
      <MiniBars
        title="Status"
        items={status.map(item => ({
          label: item.status,
          value: item.anzahl,
          text: `${item.anzahl}`,
          color: statusColor(item.status),
        }))}
      />
      <MiniBars
        title="Kündigungen"
        items={cancellations.map(item => ({
          label: item.monat,
          value: item.anzahl,
          text: `${item.anzahl}`,
          color: '#fbbf24',
        }))}
      />
    </div>
  );
}

function MiniBars({ title, items }) {
  const max = Math.max(...items.map(item => Number(item.value) || 0), 0);
  return (
    <div>
      <h3 style={{ color: '#cbd5e1', fontSize: 13, margin: '0 0 10px' }}>{title}</h3>
      {items.length === 0 ? (
        <EmptyState text="Keine Daten vorhanden." />
      ) : (
        <div style={{ display: 'grid', gap: 9 }}>
          {items.slice(0, 6).map(item => (
            <div key={item.label}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
                <span style={{ color: '#cbd5e1', fontSize: 12 }}>{item.label}</span>
                <span style={{ color: '#f8fafc', fontSize: 12, fontWeight: 700 }}>{item.text}</span>
              </div>
              <div style={barTrack}>
                <div style={{ ...barFill, width: `${max ? Math.max(6, (Number(item.value) / max) * 100) : 0}%`, background: item.color }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ color: '#38bdf8', fontSize: 18 }}>⏳ Cockpit wird geladen...</div>
    </div>
  );
}

function StateScreen({ title, text }) {
  return (
    <div style={{ padding: 24, maxWidth: 640, margin: '40px auto' }}>
      <DashboardCard title={title}>
        <EmptyState text={text} />
      </DashboardCard>
    </div>
  );
}

function EmptyState({ text }) {
  return <div style={{ color: '#94a3b8', background: '#0f172a', border: '1px dashed #334155', borderRadius: 10, padding: 16, fontSize: 14 }}>{text}</div>;
}

function eventIcon(type) {
  if (type === 'danger') return '!';
  if (type === 'warning') return '⏰';
  if (type === 'success') return '✓';
  return 'i';
}

function statusColor(status) {
  if (status === 'aktiv') return '#34d399';
  if (status === 'gekuendigt') return '#fbbf24';
  if (status === 'abgelaufen') return '#f87171';
  return '#94a3b8';
}

function formatEuro(value) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Number(value) || 0);
}

function formatDate(value) {
  if (!value) return '–';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '–';
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

const heroStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 18, flexWrap: 'wrap', background: 'linear-gradient(135deg, #172033 0%, #1e293b 62%, #10253d 100%)', border: '1px solid #334155', borderRadius: 12, padding: 22, marginBottom: 16, boxShadow: '0 18px 45px rgba(0,0,0,0.22)' };
const heroActions = { display: 'flex', gap: 10, flexWrap: 'wrap' };
const kpiGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 14 };
const kpiCard = { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 15, minHeight: 112, cursor: 'pointer', textAlign: 'left' };
const mainGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))', gap: 14, marginBottom: 14 };
const cardStyle = { background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 16, minWidth: 0, marginBottom: 14 };
const cardTitle = { color: '#f8fafc', fontSize: 16, fontWeight: 800, margin: '0 0 14px' };
const priorityDot = { width: 10, height: 10, borderRadius: 999, flex: '0 0 auto' };
const timelineIcon = { width: 30, height: 30, borderRadius: 8, display: 'grid', placeItems: 'center', fontSize: 14, fontWeight: 900, flex: '0 0 auto' };
const assistantGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 };
const barTrack = { width: '100%', height: 8, background: '#0f172a', borderRadius: 999, overflow: 'hidden' };
const barFill = { height: '100%', borderRadius: 999 };
const quickGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 };
const quickBtn = (color) => ({ padding: '10px', background: `${color}22`, border: `1px solid ${color}55`, borderRadius: 8, cursor: 'pointer', color, fontWeight: 800, fontSize: 13 });

// ─── EPIC 8: Radar-Widget ────────────────────────────────────────────────────

const RADAR_SEV = { critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e', info: '#64748b' };
const RADAR_ICON = { critical: '🔴', high: '🟠', medium: '🟡', low: '🟢', info: '⚪' };

function RadarWidget({ onNav }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    radarApi.analyse().then(setData).catch(() => {});
  }, []);

  if (!data || !data.summary) return null;
  const { summary, events } = data;
  if ((summary.critical || 0) + (summary.high || 0) + (summary.overdue || 0) === 0 && !events?.length) return null;

  const top = (events || []).filter(e => e.severity === 'critical' || e.severity === 'high' || e.horizon === 'overdue' || e.horizon === 'today').slice(0, 3);

  return (
    <div style={{ marginBottom: 16, background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>📡 Fristen & Risiken</span>
          {(summary.critical || 0) > 0 && (
            <span style={{ background: '#450a0a', color: '#fca5a5', border: '1px solid #991b1b', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12 }}>
              {summary.critical} kritisch
            </span>
          )}
          {(summary.overdue || 0) > 0 && (
            <span style={{ background: '#431407', color: '#fdba74', border: '1px solid #9a3412', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12 }}>
              {summary.overdue} überfällig
            </span>
          )}
        </div>
        <button onClick={() => onNav('/radar')} className="btn-primary">
          Radar öffnen →
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {top.map(e => {
          const color = RADAR_SEV[e.severity] || '#64748b';
          const icon = RADAR_ICON[e.severity] || '⚪';
          const days = e.daysRemaining;
          return (
            <button key={e.id} onClick={() => onNav('/radar')} style={{
              display: 'flex', alignItems: 'center', gap: 12, background: '#0f172a',
              border: `1px solid ${color}55`, borderLeft: `3px solid ${color}`,
              borderRadius: 8, padding: '10px 14px', cursor: 'pointer', textAlign: 'left', width: '100%',
            }}>
              <span style={{ fontSize: 18, minWidth: 24, textAlign: 'center' }}>{icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.contractTitle}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.description}
                </div>
              </div>
              {days !== null && (
                <span style={{ fontSize: 12, color: days < 0 ? '#ef4444' : days === 0 ? '#f97316' : '#64748b', flexShrink: 0 }}>
                  {days < 0 ? `${Math.abs(days)}d übf.` : days === 0 ? 'Heute' : `${days}d`}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {(events?.length || 0) > 3 && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', textAlign: 'center' }}>
          +{(events?.length || 0) - top.length} weitere Einträge im Radar
        </div>
      )}
    </div>
  );
}

// ─── EPIC 7: Optimierungs-Widget ──────────────────────────────────────────────

function OptimierungWidget({ onNav }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    optimierungApi.analyse().then(setData).catch(() => {});
  }, []);

  if (!data || !data.summary) return null;

  const { summary, recommendations } = data;
  if (summary.recommendations === 0) return null;

  const top = (recommendations || []).slice(0, 3);

  return (
    <div style={{ marginBottom: 16, background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>💡 Optimierungspotenzial</span>
          {summary.highPriority > 0 && (
            <span style={{ marginLeft: 8, background: '#3b0000', color: '#fca5a5', border: '1px solid #ef4444', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12 }}>
              {summary.highPriority} sofort prüfen
            </span>
          )}
        </div>
        <button onClick={() => onNav('/optimierung')} className="btn-primary">
          Alle ansehen →
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {top.map(rec => {
          const borderColor = rec.score >= 75 ? '#ef4444' : rec.score >= 50 ? '#f59e0b' : '#3b82f6';
          return (
            <button key={rec.contractId} onClick={() => onNav('/optimierung')} style={{
              display: 'flex', alignItems: 'center', gap: 12, background: '#0f172a',
              border: `1px solid ${borderColor}55`, borderLeft: `3px solid ${borderColor}`,
              borderRadius: 8, padding: '10px 14px', cursor: 'pointer', textAlign: 'left', width: '100%',
            }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: borderColor, minWidth: 36, textAlign: 'center' }}>{rec.score}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {rec.contractTitle}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                  {rec.reasons.slice(0, 2).join(' · ')}
                </div>
              </div>
              {rec.monthlyCost > 0 && (
                <span style={{ fontSize: 12, color: '#64748b', flexShrink: 0 }}>{rec.monthlyCost.toFixed(2)} €/Mo</span>
              )}
            </button>
          );
        })}
      </div>
      {summary.recommendations > 3 && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', textAlign: 'center' }}>
          +{summary.recommendations - 3} weitere Verträge mit Handlungsbedarf
        </div>
      )}
    </div>
  );
}

// ─── EPIC 13: NotificationWidget ─────────────────────────────────────────────
function NotificationWidget({ onNav }) {
  const [summary, setSummary] = useState(null);
  const [top, setTop] = useState([]);

  useEffect(() => {
    notificationsApi.summary().then(setSummary).catch(() => {});
    notificationsApi.list({ limit: 3 }).then(setTop).catch(() => {});
  }, []);

  if (!summary || summary.total === 0) return null;

  return (
    <div style={{ marginBottom: 16, background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>🔔 Erinnerungen</span>
        <button onClick={() => onNav('/benachrichtigungen')} className="btn-primary">
          Alle →
        </button>
      </div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 10, flexWrap: 'wrap' }}>
        {summary.critical > 0 && <div><span style={{ fontSize: 18, fontWeight: 700, color: '#ef4444' }}>{summary.critical}</span><div style={{ fontSize: 10, color: '#64748b' }}>Kritisch</div></div>}
        {summary.unread > 0 && <div><span style={{ fontSize: 18, fontWeight: 700, color: '#818cf8' }}>{summary.unread}</span><div style={{ fontSize: 10, color: '#64748b' }}>Ungelesen</div></div>}
        {summary.today > 0 && <div><span style={{ fontSize: 18, fontWeight: 700, color: '#22d3ee' }}>{summary.today}</span><div style={{ fontSize: 10, color: '#64748b' }}>Heute</div></div>}
      </div>
      {top.slice(0, 3).map(n => (
        <div key={n.id} style={{ fontSize: 12, color: '#94a3b8', padding: '4px 0', borderTop: '1px solid #0f172a' }}>
          {n.severity === 'CRITICAL' ? '🔴' : n.severity === 'HIGH' ? '🟠' : '🟡'} {n.title}
        </div>
      ))}
    </div>
  );
}

// ─── EPIC 12: FinancialWidget ─────────────────────────────────────────────────
function FinancialWidget({ onNav }) {
  const [baseline, setBaseline] = useState(null);

  useEffect(() => {
    financialImpactApi.baseline().then(setBaseline).catch(() => {});
  }, []);

  if (!baseline || baseline.contractCount === 0) return null;

  return (
    <div style={{ marginBottom: 16, background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>💶 Portfolio-Kosten</span>
        <button onClick={() => onNav('/simulation')} className="btn-primary">
          Simulator →
        </button>
      </div>
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#818cf8' }}>
            {(baseline.monthlyCost || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
          </div>
          <div style={{ fontSize: 10, color: '#64748b' }}>monatlich</div>
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#22d3ee' }}>
            {(baseline.annualCost || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
          </div>
          <div style={{ fontSize: 10, color: '#64748b' }}>jährlich</div>
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#94a3b8' }}>{baseline.contractCount}</div>
          <div style={{ fontSize: 10, color: '#64748b' }}>Verträge</div>
        </div>
      </div>
    </div>
  );
}

// ─── EPIC 11: ActionsWidget ───────────────────────────────────────────────────
function ActionsWidget({ onNav }) {
  const [summary, setSummary] = useState(null);
  const [top, setTop] = useState([]);

  useEffect(() => {
    actionsApi.summary().then(setSummary).catch(() => {});
    actionsApi.list({ limit: 3 }).then(d => setTop(d.actions || [])).catch(() => {});
  }, []);

  if (!summary || summary.open === 0) return null;

  const PRIO_COLOR = { CRITICAL: '#ef4444', HIGH: '#f97316', NORMAL: '#6366f1', LOW: '#22c55e' };

  return (
    <div style={{ marginBottom: 16, background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>Action Center</span>
          {summary.critical > 0 && (
            <span style={{ background: '#450a0a', color: '#fca5a5', border: '1px solid #ef4444',
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12 }}>
              {summary.critical} kritisch
            </span>
          )}
          {summary.overdue > 0 && (
            <span style={{ background: '#431407', color: '#fdba74', border: '1px solid #f97316',
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12 }}>
              {summary.overdue} überfällig
            </span>
          )}
        </div>
        <button onClick={() => onNav('/actions')} className="btn-primary">
          Alle Aufgaben →
        </button>
      </div>

      {/* Zähler-Zeile */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          { label: 'Offen', value: summary.open, color: '#94a3b8' },
          { label: 'Heute', value: summary.today, color: '#3b82f6' },
          { label: 'Diese Woche', value: summary.thisWeek, color: '#f59e0b' },
        ].map(i => i.value > 0 && (
          <div key={i.label} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: i.color }}>{i.value}</div>
            <div style={{ fontSize: 10, color: '#64748b' }}>{i.label}</div>
          </div>
        ))}
      </div>

      {/* Top-3 Actions */}
      {top.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {top.map(a => (
            <button key={a.id} onClick={() => onNav('/actions')}
              className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: PRIO_COLOR[a.priority] || '#6366f1' }} />
              <span style={{ flex: 1, minWidth: 0, color: '#f1f5f9', fontSize: 13,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {a.title}
              </span>
              {a.due_at && (
                <span style={{ fontSize: 11, color: '#64748b', flexShrink: 0 }}>
                  {a.due_at.slice(0, 10)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── EPIC 14: BankStatementWidget ────────────────────────────────────────────
function BankStatementWidget({ onNav }) {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    bankStatementsApi.summary().then(setSummary).catch(() => {});
  }, []);

  if (!summary || summary.pending_candidates === 0) return null;

  return (
    <div style={{ marginBottom: 16, background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 20 }}>🏦</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>Kontoauszüge</span>
        </div>
        <button onClick={() => onNav('/kontoauszuege')} className="btn-primary">
          Prüfen →
        </button>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {summary.new_candidates > 0 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#22c55e' }}>{summary.new_candidates}</div>
            <div style={{ fontSize: 10, color: '#64748b' }}>Neue Kandidaten</div>
          </div>
        )}
        {summary.existing_matches > 0 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#3b82f6' }}>{summary.existing_matches}</div>
            <div style={{ fontSize: 10, color: '#64748b' }}>Verträge erkannt</div>
          </div>
        )}
        {summary.price_deviations > 0 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#f59e0b' }}>{summary.price_deviations}</div>
            <div style={{ fontSize: 10, color: '#64748b' }}>Preisabweichungen</div>
          </div>
        )}
      </div>
    </div>
  );
}
